/**
 * Almacenamiento a Domicilio — frontend utility module.
 *
 * Re-exports the on-demand coverage source of truth and exposes UI-shaped
 * helpers for nav/footer visibility gating and sitemap/city-picker generation.
 *
 * Source of truth: src/constants/onDemandCoverage.ts (kept in sync with
 * spotme-server/src/constants/onDemandCoverage.ts).
 */

import {
  ON_DEMAND_COVERAGE,
  OnDemandCity,
  OnDemandCityConfig,
  ResolvedOnDemandCity,
  resolveOnDemandCity
} from 'src/constants/onDemandCoverage'
import { PillarPage } from 'src/constants/searchPages'

export {
  DEFAULT_PER_SQM_RATES,
  haversineKm,
  ON_DEMAND_COVERAGE,
  resolveOnDemandCity,
  resolveOnDemandCityByCoords
} from 'src/constants/onDemandCoverage'

export type {
  OnDemandCity,
  OnDemandCityConfig,
  OnDemandMetroAreaSlug,
  OnDemandServiceArea,
  ResolvedOnDemandCity
} from 'src/constants/onDemandCoverage'

/**
 * Returns `true` if at least one on-demand market has `partnerActive: true`.
 * Used by nav/footer to decide whether to expose the on-demand entry point at all.
 */
export function hasAnyOnDemandCoverage(): boolean {
  return Object.values(ON_DEMAND_COVERAGE).some(
    (config) => config.partnerActive
  )
}

/**
 * Returns the list of on-demand markets currently exposed to users.
 * Used by sitemap generation, the city picker, and any surface that lists active markets.
 */
export function getActiveOnDemandCities(): OnDemandCityConfig[] {
  return Object.values(ON_DEMAND_COVERAGE).filter(
    (config) => config.partnerActive
  )
}

/**
 * Convenience: resolves a slug and returns just the canonical city, or `null`
 * if the slug doesn't map to a partner-active market.
 *
 * Surfaces that need to gate on `partnerActive` should still call
 * `resolveOnDemandCity()` directly to access the full config.
 */
export function resolveActiveOnDemandCity(
  slug: string | undefined | null
): OnDemandCity | null {
  const resolved: ResolvedOnDemandCity | null = resolveOnDemandCity(slug)
  if (!resolved || !resolved.partnerActive) return null
  return resolved.city
}

/**
 * The on-demand photo set — optimized derivatives of the branded
 * `delivery-*.png` renders (~2 MB PNGs → ~120-250 KB JPEGs, same 1448px
 * width). The PNG originals stay in `public/images/` because compiled
 * emails (`emails/out/*`) and `scripts/generate-og-cards.mjs` still point at
 * them; web surfaces use these.
 *
 * Every landing section draws from this one map so no page renders the same
 * photo twice. Allocation per landing variant:
 *
 * | Section                      | Monterrey        | CDMX            | Parent          |
 * | ---------------------------- | ---------------- | --------------- | --------------- |
 * | Hero                         | monterreySkyline | loadingBay      | crewBoxVan      |
 * | Cómo funciona (4 steps)      | truck · crewBoxesHome · secureGarage · handoff (all variants) |
 * | Photo break (full-bleed)     | crewBoxVan       | crewBoxVan      | parkingLot      |
 * | Coverage zones               | parkingLot       | parkingLot      | — (not shown)   |
 * | City-picker tiles            | — (not shown)    | — (not shown)   | each city's hero |
 *
 * Regenerate with `magick delivery-N.png -strip -interlace Plane
 * -sampling-factor 4:2:0 -quality 80 domicilio/<name>.jpg`.
 */
const ON_DEMAND_PHOTOS = {
  truck: '/images/domicilio/truck-street.jpg',
  loadingBay: '/images/domicilio/van-loading-bay.jpg',
  parkingLot: '/images/domicilio/van-parking-lot.jpg',
  secureGarage: '/images/domicilio/van-secure-garage.jpg',
  monterreySkyline: '/images/domicilio/van-monterrey-skyline.jpg',
  crewBoxesHome: '/images/domicilio/crew-boxes-home.jpg',
  crewBoxVan: '/images/domicilio/crew-box-van.jpg',
  handoff: '/images/domicilio/handoff-customer.jpg'
} as const

/**
 * One photo per `PROCESS.STEP_n`, in order: agenda (truck on its way) →
 * recogemos (crew loading boxes at a home) → cuidamos (van in a secure
 * garage) → entregamos (box handed to the customer).
 */
export const ON_DEMAND_STEP_IMAGES: readonly string[] = [
  ON_DEMAND_PHOTOS.truck,
  ON_DEMAND_PHOTOS.crewBoxesHome,
  ON_DEMAND_PHOTOS.secureGarage,
  ON_DEMAND_PHOTOS.handoff
]

/**
 * Photo for the wide on-demand card surfaces — empty-state recovery card,
 * pillar cross-link card, directory ficha card. Always a person with boxes
 * (the one-glance "we come to you" read), full landscape frame; those
 * cards run 16:10 → 5:2, so a square crop would be upscaled and cut at the
 * head. The narrow search-grid tile uses `getOnDemandCardThumb()` instead.
 * The per-city landing hero is a different brief — see
 * `getOnDemandHeroImage()`.
 */
export function getOnDemandImage(citySlug: string | null | undefined): string {
  switch (citySlug) {
    case 'monterrey':
      return ON_DEMAND_PHOTOS.crewBoxesHome
    case 'cdmx':
      return ON_DEMAND_PHOTOS.crewBoxVan
    default:
      return ON_DEMAND_PHOTOS.handoff
  }
}

/**
 * Square crop of `getOnDemandImage()`'s photo, centred on the person
 * (`card-*.jpg`, 800×800), for the search-grid tile, whose image rail is
 * only 130-200px wide and taller than it is wide. A 4:3 frame there shows a
 * slice of van; the square crop keeps the face and the box.
 */
export function getOnDemandCardThumb(
  citySlug: string | null | undefined
): string {
  switch (citySlug) {
    case 'monterrey':
      return '/images/domicilio/card-monterrey.jpg'
    case 'cdmx':
      return '/images/domicilio/card-cdmx.jpg'
    default:
      return '/images/domicilio/card-default.jpg'
  }
}

/**
 * Hero photo per landing variant. MTY leans on the Cerro de la Silla
 * skyline (the page header already told the visitor which city they're
 * on); CDMX gets the loading bay; the parent page gets the crew shot.
 * Also used by the parent page's city-picker tiles.
 */
export function getOnDemandHeroImage(
  citySlug: string | null | undefined
): string {
  switch (citySlug) {
    case 'monterrey':
      return ON_DEMAND_PHOTOS.monterreySkyline
    case 'cdmx':
      return ON_DEMAND_PHOTOS.loadingBay
    default:
      return ON_DEMAND_PHOTOS.crewBoxVan
  }
}

/**
 * Social-card variant of `getOnDemandHeroImage()` — same photograph, cropped to
 * 1.91:1 and re-encoded as JPEG.
 *
 * Do NOT point `og:image` at the `delivery-*.png` originals. They are ~1.7-2.3MB
 * 4:3 PNGs: WhatsApp's link-preview fetcher will not render a source that large,
 * so the page unfurls as a bare text link in SpotMe's single biggest demand
 * channel, and Meta/LinkedIn centre-crop a quarter of the 4:3 frame away.
 * These derivatives are ~100-150KB. Regenerate with the crops recorded in
 * `scripts/generate-og-cards.mjs`.
 */
export function getOnDemandSocialImage(
  citySlug: string | null | undefined
): string {
  switch (citySlug) {
    case 'monterrey':
      return '/images/og/og-domicilio-monterrey.jpg'
    case 'cdmx':
      return '/images/og/og-domicilio-cdmx.jpg'
    default:
      return '/images/og/og-domicilio.jpg'
  }
}

/**
 * Secondary photo for `CoverageZones` (city pages only — the parent page
 * has no coverage list). Every covered city shares it today; add a city
 * parameter back when one needs its own shot. See the allocation table on
 * `ON_DEMAND_PHOTOS`.
 */
export function getOnDemandSecondaryImage(): string {
  return ON_DEMAND_PHOTOS.parkingLot
}

/**
 * Photo for the full-bleed "Tú abres la puerta" break. City pages use the
 * crew shot (their heroes carry no person); the parent page, whose hero IS
 * the crew shot and whose city-picker tiles already show the MTY skyline and
 * the CDMX loading bay, falls back to the parking-lot fleet shot.
 */
export function getOnDemandBreakImage(
  citySlug: string | null | undefined
): string {
  switch (citySlug) {
    case 'monterrey':
    case 'cdmx':
      return ON_DEMAND_PHOTOS.crewBoxVan
    default:
      return ON_DEMAND_PHOTOS.parkingLot
  }
}

/**
 * Open-redirect guard for the `searchReturn` query param.
 *
 * Returns `true` only when the path is a same-origin relative URL safe to use
 * as a `<Link href>` or `router.push` argument.
 *
 * Rejects:
 *  - Empty / non-string values.
 *  - Anything that doesn't start with `/` (relative paths, full URLs).
 *  - `//evil.com` (protocol-relative — would resolve to a third-party origin).
 *  - `/\` and friends (rare but valid attack vectors per OWASP open-redirect).
 *  - Paths containing `://` (a malformed URL slipping through encoding).
 *
 * Used to validate the `searchReturn` param the on-demand cards append so a
 * crafted URL like `/almacenamiento-a-domicilio/monterrey?searchReturn=//evil.com`
 * can't trick the back-button affordance into navigating off-site.
 */
export function isSafeReturnPath(
  path: string | string[] | undefined | null
): path is string {
  if (typeof path !== 'string') return false
  if (path.length === 0) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  if (path.startsWith('/\\')) return false
  if (path.includes('://')) return false
  return true
}

/**
 * Build the on-demand landing-page href for a given canonical city.
 *
 * When `currentAsPath` is provided AND passes {@link isSafeReturnPath}, the
 * returned URL carries a `searchReturn` query param holding the encoded
 * originating path. The landing page reads this param to render a "Volver a
 * tus resultados" affordance, preserving the search-→-on-demand-→-search
 * round trip without depending on `document.referrer` (which gets stripped
 * by some CSPs and isn't available SSR).
 *
 * Mirror of the search-card transit pattern in `LocationSearchCard.getUrl()`:
 * the originating URL is opaque to the destination page beyond the back link.
 */
export function buildOnDemandHref(
  canonicalCity: OnDemandCity,
  currentAsPath?: string | undefined
): string {
  const base = `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${canonicalCity}`
  if (!currentAsPath || !isSafeReturnPath(currentAsPath)) return base
  return `${base}?searchReturn=${encodeURIComponent(currentAsPath)}`
}
